import { useEffect, useState } from "react";
import type { NotificationsResponseDto } from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchAdminNotifications } from "../../shared/api/notification";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { NotificationCard } from "../../shared/ui/NotificationCard";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";

function buildAdminLinks(labels = GENERIC_TENANT_LABELS) {
  return [
  { to: "/t/:tenantSlug/teacher", label: `${labels.teacher}ホームへ戻る` },
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" }
];
}

type AdminMenuItem = {
  to: string;
  label: string;
  summary: string;
  status: string;
};

function buildAdminMenuItems(labels = GENERIC_TENANT_LABELS): AdminMenuItem[] {
  return [
    {
      to: "/t/:tenantSlug/teacher/admin/members",
      label: `メンバー / ${labels.teacher}`,
      summary: "メンバー一覧・作成・権限更新",
      status: "API接続済み"
    },
    {
      to: "/t/:tenantSlug/teacher/admin/students",
      label: labels.student,
      summary: `${labels.student}の検索・一覧・登録`,
      status: "API接続済み"
    },
    {
      to: "/t/:tenantSlug/teacher/admin/family-accounts",
      label: labels.familyAccount,
      summary: "家族単位の確認・登録",
      status: "API接続済み"
    },
    {
      to: "/t/:tenantSlug/teacher/admin/recurring-slots",
      label: labels.studio,
      summary: `${labels.studio}作成・一覧`,
      status: "API接続済み"
    },
    {
      to: "/t/:tenantSlug/teacher/admin/lessons",
      label: labels.lesson,
      summary: `通常${labels.lesson} / 短期${labels.lesson} / 定期枠`,
      status: "API接続済み"
    },
    {
      to: "/t/:tenantSlug/teacher/admin/enrollments",
      label: labels.enrollment,
      summary: `${labels.enrollment}作成・一覧`,
      status: "API接続済み"
    },
    {
      to: "/t/:tenantSlug/teacher/admin/schedule",
      label: "スケジュール",
      summary: "月次スケジュール・予定管理",
      status: "API接続済み"
    },
    {
      to: "/t/:tenantSlug/teacher/admin/guardian-links",
      label: "保護者紐付け・招待",
      summary: "保護者招待・申請確認・家族側利用確認",
      status: "API接続済み"
    },
    {
      to: "/t/:tenantSlug/teacher/admin/member-communications",
      label: "連絡事項",
      summary: "テキスト・画像・動画・PDFの連絡を作成・公開",
      status: "API接続済み"
    },
    {
      to: "/t/:tenantSlug/teacher/admin/tenant",
      label: "テナント",
      summary: "クラブ名・公開設定・運用設定",
      status: "API接続済み"
    },
    {
      to: "/t/:tenantSlug/teacher/admin/public-settings",
      label: "公開設定",
      summary: "公開TOP・スケジュール・イベント表示設定",
      status: "API接続済み"
    },
    {
      to: "/t/:tenantSlug/teacher/admin/public-events",
      label: "公開イベント",
      summary: "公開event 作成 / 編集 / 公開期間設定",
      status: "API接続済み"
    },
    {
      to: "/t/:tenantSlug/teacher/admin/integrations",
      label: "連携設定",
      summary: "LINE / メール / ストレージ / AI などの接続状態",
      status: "API接続済み"
    },
    {
      to: "/t/:tenantSlug/teacher/admin/billing",
      label: "請求",
      summary: "生徒向け月謝・支払者・請求単位の確認と管理",
      status: "API接続済み"
    },
    {
      to: "/t/:tenantSlug/teacher/admin/contract",
      label: "契約・解約",
      summary: "契約情報・契約メール・料金方式・支払カード・解約予約",
      status: "API接続済み"
    }
  ];
}

export function AdminHomePage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [notifications, setNotifications] = useState<NotificationsResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    let cancelled = false;

    async function loadNotifications() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextNotifications = await fetchAdminNotifications(tenantSlug);

        if (cancelled) {
          return;
        }

        setNotifications(nextNotifications);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }

        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "admin menu / notifications の取得に失敗しました"));
      }
    }

    void loadNotifications();

    return () => {
      cancelled = true;
    };
  }, [hasAdminRole, isSessionReady, navigate, tenantSlug]);

  const resolvedAdminMenuItems = buildAdminMenuItems(tenantLabels);

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="管理ホーム"
      description="管理者向けの運用メニューと運用アラートを表示します。"
      links={buildAdminLinks(tenantLabels)}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>管理者向け通知を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}

        {isSessionReady && hasAdminRole ? (
          <>
            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>管理メニュー</h2>
              <div style={{ display: "grid", gap: 12 }}>
                {resolvedAdminMenuItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to.replace(":tenantSlug", tenantSlug)}
                    style={{
                      display: "grid",
                      gap: 8,
                      padding: 12,
                      border: "1px solid #e5e5e5",
                      borderRadius: 12,
                      textDecoration: "none",
                      color: "inherit"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                      <strong>{item.label}</strong>
                      <span
                        style={{
                          fontSize: 12,
                          opacity: 0.8,
                          padding: "4px 8px",
                          borderRadius: 999,
                          background:
                            item.status === "API接続済み"
                              ? "#eef6ff"
                              : item.status === "有料機能" || item.status === "準備中"
                                ? "#fff7ed"
                              : "#f3f3f3"
                        }}
                      >
                        {item.status}
                      </span>
                    </div>
                    <div style={{ fontSize: 14, opacity: 0.8 }}>{item.summary}</div>
                  </Link>
                ))}
              </div>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>確認できること</h2>
              <ul style={{ margin: 0, paddingLeft: 20, display: "grid", gap: 6 }}>
                <li>月次スケジュールと予定の再生成・確認・公開</li>
                <li>保護者招待の発行・再発行・失効と家族側での招待利用</li>
                <li>メンバー権限追加、{tenantLabels.studio}作成、{tenantLabels.student}・{tenantLabels.familyAccount}・{tenantLabels.enrollment}情報の作成</li>
                <li>通常{tenantLabels.lesson} / 短期{tenantLabels.lesson}作成、定期枠と個別予定の確認</li>
                <li>管理者による連絡事項の作成・添付ファイルアップロード・公開</li>
                <li>管理者向け通知の表示</li>
              </ul>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gap: 4 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>運用アラート</h2>
                <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                  テナント全体の運用アラートをカテゴリ・優先度・時刻付きで表示します。
                </p>
              </div>
              {notifications?.items.length === 0 ? <p style={{ margin: 0 }}>運用アラートはありません。</p> : null}
              {notifications?.items.map((item) => (
                <NotificationCard key={item.itemId} item={item} labels={tenantLabels} />
              ))}
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}

export { AdminRecurringSlotDetailPage } from "./AdminRecurringSlotDetailPage";

export { AdminBillingPage } from "./AdminBillingPage";
export { AdminBillingLessonFeesPage, AdminBillingLessonFeeDetailPage } from "./AdminBillingLessonFeesPages";
export { AdminBillingEventFeesPage, AdminBillingEventFeeDetailPage } from "./AdminBillingEventFeesPages";
export { AdminBillingMonthlyPage, AdminBillingMonthlyStudentPage } from "./AdminBillingMonthlyPages";
export { AdminBillingManualItemsPage, AdminBillingManualItemDetailPage } from "./AdminBillingManualItemsPages";
export { AdminBulkEnrollmentCreatePage } from "./AdminBulkEnrollmentCreatePage";
export { AdminContractPage } from "./AdminContractPage";
export { AdminContractCancellationPage } from "./AdminContractCancellationPage";
export { AdminContractCancellationRevocationPage } from "./AdminContractCancellationRevocationPage";
export { AdminEnrollmentsPage } from "./AdminEnrollmentsPage";
export { AdminEventsPage } from "./AdminEventsPage";
export { AdminGuardianLinkRequestsPage } from "./AdminGuardianLinkRequestsPage";
export { AdminGuardianLinksPage } from "./AdminGuardianLinksPage";
export { AdminIntegrationsPage } from "./AdminIntegrationsPage";
export { AdminLessonDetailPage } from "./AdminLessonDetailPage";
export { AdminLessonsPage } from "./AdminLessonsPage";
export { AdminMembersPage } from "./AdminMembersPage";
export { AdminMemberCommunicationsPage } from "./AdminMemberCommunicationsPage";
export { AdminOccurrenceDetailPage } from "./AdminOccurrenceDetailPage";
export { AdminPoliciesPage } from "./AdminPoliciesPage";
export { AdminPublicEventsPage } from "./AdminPublicEventsPage";
export { AdminPublicSettingsPage } from "./AdminPublicSettingsPage";
export { AdminRecurringSlotsPage } from "./AdminRecurringSlotsPage";
export { AdminScheduleEntryPage } from "./AdminScheduleEntryPage";
export { AdminScheduleMonthPage } from "./AdminScheduleMonthPage";
export { AdminTenantSettingsPage } from "./AdminTenantSettingsPage";
export { AdminFamilyAccountCreatePage } from "./AdminFamilyAccountCreatePage";
export { AdminFamilyAccountDetailPage } from "./AdminFamilyAccountDetailPage";
export { AdminFamilyAccountsPage } from "./AdminFamilyAccountsPage";
export { AdminStudentCreatePage } from "./AdminStudentCreatePage";
export { AdminStudentDetailPage } from "./AdminStudentDetailPage";
export { AdminStudentsPage } from "./AdminStudentsPage";
export { AdminVideosPage } from "./AdminVideosPage";
